import axios from "axios";
import { CityKey } from "@/features/services";

/**
 * bk-courier-assistant («Помощник курьера»). Контракт — docs/API.md в его репо.
 */
const $bkca = axios.create({
  baseURL: __BKCA_URL__,
  timeout: 45000,
});

$bkca.interceptors.request.use((config) => {
  if (__BKCA_TOKEN__) {
    config.headers.Authorization = `Bearer ${__BKCA_TOKEN__}`;
  }
  return config;
});

export type AskInclude = "context" | "series" | "evidence";

export interface AskRequest {
  question: string;
  user_id: string;
  city?: CityKey | null;
  tariffs?: number[];
  include?: AskInclude[];
}

export interface Signal {
  kind: string;
  kind_ru: string;
  city: string;
  tariffs: number[];
  place?: string | null;
  confidence: number;
  confidence_base?: number;
  witnesses: number;
  valid_from?: string;
  valid_to?: string;
  upcoming?: boolean;
  days_ahead?: number;
  evidence?: {
    chat: string;
    text: string;
    sent_at: string;
    voice?: boolean;
  }[];
}

export interface AskContext {
  data: {
    days: number;
    built_at: string;
    income_until_utc: string | null;
    orders_until_utc: string | null;
    income_lag_hours: number | null;
  };
  resolved: { city: string | null; tariffs: number[]; intent: string; branch: string };
  numbers: number[];
  guard: { allowed_numbers: number; blocked: string | null };
  signals: Signal[];
  personal: boolean;
  zones: boolean;
  steps: string[];
}

export interface SeriesTariff {
  tariff: number;
  tariff_ru: string;
  overall: number | null;
  idle_share: number | null;
  today: number | null;
  today_hours: number | null;
  price_ratio: number | null;
  by_hour: (number | null)[];
  by_hour_orders: (number | null)[];
  by_dow: (number | null)[];
  /** Загрузка часа относительно пика, 0…1. */
  load: (number | null)[];
  best_hours: number[];
  worst_hours: number[];
}

export interface AskSeries {
  city: string;
  hours: number[];
  dow: string[];
  tariffs: SeriesTariff[];
}

export interface AskResponse {
  text: string;
  branch: "template" | "llm" | "redirect" | "no_data" | string;
  intent: string;
  numbers: number[];
  city: string | null;
  tariffs: number[];
  why: string;
  blocked: string | null;
  context?: AskContext;
  series?: AskSeries;
}

export async function ask(
  body: AskRequest,
  options: { timeout?: number } = {}
): Promise<AskResponse> {
  const { data } = await $bkca.post<AskResponse>("/ask", body, options);
  return data;
}

export interface MetaResponse {
  version: string;
  cities: Record<string, { tz_offset: number }>;
  tariffs: Record<string, string>;
  intents: Record<string, string>;
  signal_kinds: Record<string, string>;
  include: string[];
  examples: string[];
}

export async function getMeta(): Promise<MetaResponse> {
  const { data } = await $bkca.get<MetaResponse>("/meta");
  return data;
}
